/**
 * WebSocket Connection Singleton
 * Maintains WebSocket connection across React component unmount/mount cycles
 * Uses Ably Native Pub/Sub with automatic reconnection and message recovery
 */

import { createEchoInstance } from './ably-init';
import type { TypedEchoInstance } from './types/echo.types';
import { wsLogger } from '@/shared/lib/logger';

let echoInstance: TypedEchoInstance | null = null;
let currentUserId: string | null = null;
let currentToken: string | null = null;

/**
 * Get or create Echo instance
 * Returns existing instance if credentials match, otherwise creates new one
 */
export function getOrCreateEchoInstance(
  userId: string,
  token: string
): TypedEchoInstance {
  // If credentials changed, disconnect old instance
  if (echoInstance && (currentUserId !== userId || currentToken !== token)) {
    echoInstance.disconnect();
    echoInstance = null;
  }

  // Create new instance if needed
  if (!echoInstance) {
    echoInstance = createEchoInstance(token);
    currentUserId = userId;
    currentToken = token;
  }

  return echoInstance;
}

/**
 * Disconnect and clear singleton instance
 * Called on logout or when user changes
 */
export function disconnectEchoSingleton(): void {
  if (echoInstance) {
    wsLogger.info('Disconnecting Echo singleton instance');
    echoInstance.disconnect();
    echoInstance = null;
    currentUserId = null;
    currentToken = null;
  }
}

/**
 * Get current Echo instance without creating new one
 */
export function getCurrentEchoInstance(): TypedEchoInstance | null {
  return echoInstance;
}

/**
 * Check if Echo instance exists
 */
export function hasEchoInstance(): boolean {
  return echoInstance !== null;
}
